/**
 * Что говорить оператору про подписку трейдера на рынок.
 *
 * Существует потому, что `tracked=false` — это НЕСКОЛЬКО разных состояний, и
 * большинство из них — норма, а не сбой:
 *
 *   - окно серии ещё не открылось: трейдер сознательно не подписан, потому что
 *     пустой стакан только удваивает поток дельт (подписка начинается за
 *     MD_WINDOW_LEAD до открытия);
 *   - окно уже идёт, но флаг прочитан ДО открытия: страница сеет рынок из кэша
 *     списка, и первые секунды живого окна флаг честно отражает прошлое, а не
 *     настоящее;
 *   - окно закончилось: отписка после конца — штатное освобождение слота.
 *
 * Пока все эти состояния рисовались одной жёлтой плашкой «не отслеживается»,
 * оператор на каждом роловере видел тревогу на исправной системе — и учился её
 * игнорировать, то есть плашка переставала работать там, где она правда нужна:
 * рынок вне состава подписки или отказ справочника на роловере.
 */

import { parseSeriesSlug, windowPhase } from "./series";

export interface TrackingNotice {
  /** alarm — жёлтая тревога: стакана нет там, где он обязан быть.
   *  info — приглушённая справка: так и задумано, просто подожди. */
  tone: "alarm" | "info";
  /** Короткий текст: в шапку рынка и в подзаголовок формы ордера. */
  text: string;
  /** Развёрнутое объяснение — в подсказку при наведении. */
  title: string;
}

/**
 * Решает, какую пометку о подписке показать.
 *
 * `fetchedAt` — момент, когда флаг подписки ЧИТАЛСЯ (мс). Без него не отличить
 * «трейдер не подписался на живое окно» от «мы смотрим на кэш, снятый до
 * открытия»: тревога допустима только по данным, добытым после открытия окна.
 */
export function trackingNotice(
  slug: string,
  tracked: boolean,
  now: Date,
  fetchedAt: number,
): TrackingNotice | null {
  if (tracked) return null;

  const parts = parseSeriesSlug(slug);
  if (!parts) {
    // Рынок вне серий (выборы, макрособытия): расписания нет, ждать нечего —
    // отсутствие подписки означает, что рынка нет в составе. Это и есть
    // исходный смысл плашки.
    return {
      tone: "alarm",
      text: "не отслеживается",
      title:
        "трейдер не подписан на этот рынок: стакана по нему нет и не будет. " +
        "Состав подписки задаётся MD_TRACK_SERIES и MD_TRACK_MARKETS",
    };
  }

  switch (windowPhase(parts, now)) {
    case "upcoming":
      return {
        tone: "info",
        text: "подписка появится к открытию",
        title:
          "окно ещё не открылось, и трейдер сознательно на него не подписан: " +
          "пустой стакан только удваивает поток дельт. Подписка обязана " +
          "появиться не позже чем за 45 секунд до открытия (MD_WINDOW_LEAD)",
      };
    case "live":
      if (fetchedAt < parts.start.getTime()) {
        return {
          tone: "info",
          text: "проверяю подписку…",
          title:
            "флаг подписки прочитан до открытия окна и мог устареть; " +
            "справочник сейчас перечитывается",
        };
      }
      return {
        tone: "alarm",
        text: "не отслеживается",
        title:
          "окно идёт, а трейдер на него не подписан: стакана нет. Так выглядит " +
          "отказ справочника Gamma на роловере — смотрите логи трейдера. " +
          "Состав подписки задаётся MD_TRACK_SERIES и MD_TRACK_MARKETS",
      };
    case "past":
      // Отписка от закончившегося окна — норма, слот освобождён под живое.
      // «Окно закончилось» уже говорят шапка и плашка WindowExpired; жёлтое
      // «не отслеживается» поверх них — ложная тревога на каждом прошедшем окне.
      return null;
  }
}

/** За сколько до открытия окна начинать частый опрос флага.
 *
 *  Число выведено из ПОТОЛКА настройки трейдера, а не из её текущего значения:
 *  MD_WINDOW_LEAD зажат двумя минутами (config.MarketData.normalize), и запас
 *  в две с половиной минуты покрывает любую допустимую настройку с тридцатью
 *  секундами на дорогу. Прежняя кратность «вчетверо над умолчанием» этого не
 *  давала: с поднятым до минуты умолчанием опрос начинался ровно в секунду
 *  подписки, а на стенде (120s) пометка «подписка появится к открытию» висела
 *  бы целую минуту на уже подписанном окне — то есть ровно в те секунды, ради
 *  которых запас и поднимали. */
const LEAD_GUARD_MS = 150_000;

/** Частота опроса, пока флаг вот-вот должен смениться. */
const POLL_MS = 3_000;

/**
 * Как часто перечитывать рынок ради флага подписки. `false` — не перечитывать.
 *
 * Флаг живёт в кэше страницы до полминуты, а меняется за секунды до открытия
 * окна. Без опроса первые полминуты живого окна страница показывала бы вчерашний
 * `tracked=false` — ту самую ложную тревогу. Опрашивается ОДИН рынок, а не весь
 * справочник: справочник отдаёт сотни строк, и учащать его опрос нельзя
 * (см. RefreshMarkets в SeriesNav).
 */
export function trackingPollMs(slug: string, tracked: boolean, nowMs: number): number | false {
  // Подписка есть — снимать её некому, кроме конца окна, а конец и так виден
  // по часам без перечитывания справочника.
  if (tracked) return false;

  const parts = parseSeriesSlug(slug);
  // Вне серии расписания нет: флаг сменится только правкой конфигурации, и
  // ловить её опросом бессмысленно — хватает получасового опроса списка.
  if (!parts) return false;

  if (nowMs >= parts.end.getTime()) return false;

  const guard = parts.start.getTime() - LEAD_GUARD_MS;
  if (nowMs >= guard) return POLL_MS;
  // До окна ещё долго: одно пробуждение ровно к началу частого опроса.
  return guard - nowMs;
}
